import { useEffect, useMemo, useRef, useState } from 'react';
import PhotoViewer from '../PhotoViewer';
import { photoTypeLabel } from '../../utils/photoCapture';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { can } from '../../utils/permissions';
import ReferenceVetDialog from '../ai/ReferenceVetDialog';
import { galleryTarget } from './quickActions';
import { Code, Taxon } from './cells';
import * as S from '../specimens/styles';

/*
 * A row's photograph, opened large: the hub's lightbox.
 *
 * Pressing the picture in the table's Photo column opens it at once -- the
 * row already has the picture (its thumbnail_url, the cap-from-above photo's
 * small copy), so nothing waits on the server -- while the isolate's dossier
 * is read (useDossiers.ensure; an expanded row shares the same copy). When
 * it arrives, the whole set takes the picture's place: every photograph of
 * the isolate -- its collection's or intake's, its plates' and its tubes'
 * (routes/specimen_overview.py::_photos) -- opened on the one the row showed,
 * else the first, with previous/next across them.
 *
 * It is PhotoViewer with the hub's words under the photograph: what the
 * photograph is (its view) and of which record (a field photograph of the
 * collection or intake, a dish photograph of plate …-P1, a tube), whose it is
 * (the isolate's taxon, ISO code and lab code, then the name its collector
 * gave it and where it was found), when and where it was taken (the viewer's
 * own capture line), its notes, when it was uploaded, and two ways out:
 * "Open original" (the file) and "Open gallery" -- the destination the
 * picture used to link to (quickActions.galleryTarget: the owner's Field
 * gallery, the collection's page, or the intake's record), reached through
 * the hub's onNavigate, closing the lightbox. Who may vet (reference:vet) can
 * make a field photograph a reference of a species (ReferenceVetDialog, L12).
 *
 * Under it, the strip of every photograph (PhotoViewer's `strip`), and once
 * the set has more than one kind, a chip per kind -- Field, Dishes, Tubes,
 * with how many -- that goes to the first of its kind: the field photographs
 * come first (the dossier's order), so what the specimen looked like when it
 * was collected is where the set opens.
 *
 * A dossier that cannot be read is said under the picture, with a retry
 * (dossiers.refresh); the row's picture stays open meanwhile. A dossier
 * whose photographs are hidden from this reader (an origin out of their
 * scope) leaves the row's picture as the only one, and says so.
 *
 * Accessibility is PhotoViewer's: a labelled modal dialog, focus moved in
 * and returned to the thumbnail button (`returnFocusTo`), Tab kept inside,
 * Escape closes.
 *
 * Props:
 *   item          the overview row
 *   dossiers      the hub's useDossiers() (get / ensure / refresh)
 *   user, t       the hub's
 *   onClose
 *   onNavigate    the hub's `go` (path, state)
 *   returnFocusTo the thumbnail button that opened it
 */
export default function IsolateLightbox({ item, dossiers, user, t, onClose, onNavigate, returnFocusTo = null }) {
  const id = item.specimen_id;
  const { ensure, refresh } = dossiers;
  useEffect(() => { ensure(id); }, [ensure, id]);
  const entry = dossiers.get(id);
  const dossier = entry.data;

  // The row's own picture, open from the first paint.
  const own = useMemo(() => (item.thumbnail_url
    ? [{ photo_id: `row-${id}`, s3_url: item.thumbnail_url, thumbnail_url: item.thumbnail_url, photo_type: null, provisional: true }]
    : []), [item.thumbnail_url, id]);
  const full = useMemo(() => (dossier?.photos || []).filter((p) => p && p.s3_url), [dossier]);
  const photos = full.length ? full : own;

  // Once the set arrives it opens on the row's picture; after that the
  // reader's own stepping is kept, whatever the dossier does.
  const [index, setIndex] = useState(0);
  const [vetting, setVetting] = useState(null);
  const swapped = useRef(false);
  useEffect(() => {
    if (swapped.current || !full.length) return;
    swapped.current = true;
    const at = full.findIndex((p) => sameUrl(p.thumbnail_url, item.thumbnail_url) || sameUrl(p.s3_url, item.thumbnail_url));
    setIndex(at >= 0 ? at : 0);
  }, [full, item.thumbnail_url]);
  const safeIndex = Math.min(Math.max(0, index), Math.max(0, photos.length - 1));

  // The kinds of photograph in the set, in its order, and where each starts.
  const groups = useMemo(() => {
    const out = [];
    full.forEach((p, i) => {
      const kind = GROUP_OF[p.source];
      if (!kind) return;
      const known = out.find((g) => g.kind === kind);
      if (known) known.count += 1;
      else out.push({ kind, first: i, count: 1 });
    });
    return out;
  }, [full]);

  const target = galleryTarget(item, user);
  const openGallery = () => {
    onClose();
    if (target) onNavigate(target.path, target.state);
  };

  if (!photos.length) return null;

  // The first line: the photograph's view, and the record it was taken of.
  const head = (photo) => {
    const of = photo.provisional ? null : RECORD_OF[photo.source];
    return (
      <div data-testid="hub-lightbox-head">
        <strong>{photo.photo_type ? photoTypeLabel(t, photo.photo_type) : t('lightbox.photo')}</strong>
        {of && (
          <span style={{ opacity: 0.85 }}>
            {' · '}{t(`lightbox.of.${of}`)}{photo.record_code ? <> <Code>{photo.record_code}</Code></> : null}
          </span>
        )}
      </div>
    );
  };

  // Whose it is, when it was uploaded, and how the set is doing.
  const caption = (photo) => (
    <>
      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} data-testid="hub-lightbox-isolate">
        {item.taxon_name && <><Taxon name={item.taxon_name} />{' · '}</>}
        <Code strong>{item.specimen_code}</Code>
        {item.lab_code && <>{' · '}<Code>{item.lab_code}</Code></>}
      </div>
      {(item.given_name || item.location_name) && (
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} data-testid="hub-lightbox-nameplace">
          {item.given_name && <strong dir="auto">{item.given_name}</strong>}
          {item.given_name && item.location_name && ' · '}
          {item.location_name && <><span aria-hidden="true">📍</span> <bdi>{item.location_name}</bdi></>}
        </div>
      )}
      {groups.length > 1 && (
        <div role="group" aria-label={t('lightbox.groups')} data-testid="hub-lightbox-groups"
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {groups.map((g) => {
            const on = GROUP_OF[photos[safeIndex]?.source] === g.kind;
            return (
              <button key={g.kind} type="button" aria-pressed={on} onClick={() => setIndex(g.first)}
                title={t('tip.lightboxGroup')} style={on ? groupOn : ghost} data-testid={`hub-lightbox-group-${g.kind}`}>
                {t(`lightbox.group.${g.kind}`)} <span style={{ opacity: 0.8 }}>({g.count})</span>
              </button>
            );
          })}
        </div>
      )}
      {photo.uploaded_at && !photo.provisional && (
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} data-testid="hub-lightbox-uploaded">
          {t('lightbox.uploaded')} {moment(photo.uploaded_at)}
        </div>
      )}
      {entry.loading && !dossier && (
        <div role="status" style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} data-testid="hub-lightbox-loading">
          {t('lightbox.loading')}
        </div>
      )}
      {entry.error && !dossier && (
        <div role="alert" style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
          data-testid="hub-lightbox-error">
          <span>{t('lightbox.loadFailed')} {entry.error}</span>
          <button type="button" style={ghost} onClick={() => refresh(id)} title={t('tip.retry')}
            data-testid="hub-lightbox-retry">{t('retry')}</button>
        </div>
      )}
      {dossier && !full.length && (
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} data-testid="hub-lightbox-only">{t('lightbox.onlyThumbnail')}</div>
      )}
    </>
  );

  const actions = (photo) => (
    <>
      {target && (
        <button type="button" style={ghost} onClick={openGallery} title={t('tip.lightboxGallery')}
          data-testid="hub-lightbox-gallery">
          {t('lightbox.openGallery')}
        </button>
      )}
      {can(user, 'reference:vet') && mayBeReference(photo) && (
        <button type="button" style={ghost} onClick={() => setVetting(photo)} title={t('tip.lightboxVet')}
          data-testid="hub-lightbox-vet">
          {t('lightbox.vet')}
        </button>
      )}
      {vetting && (
        <ReferenceVetDialog photoId={vetting.photo_id} taxon={item.taxon_name || ''} onClose={() => setVetting(null)} />
      )}
    </>
  );

  return (
    <PhotoViewer photos={photos} index={safeIndex} onIndex={setIndex} onClose={onClose}
      dialogLabel={t('lightbox.title', { code: item.specimen_code })} renderHead={head}
      extraCaption={caption} extraActions={actions} testId="hub-lightbox" returnFocusTo={returnFocusTo} strip />
  );
}

/** A field or intake photograph of the specimen itself, the only kind the store takes
 *  (routes/reference_vectors.py REASONS_SQL says so again, and has the last word). */
function mayBeReference(photo) {
  return !photo.provisional && (photo.source === 'collection' || photo.source === 'external')
    && !['microscopy', 'plate_photo', 'fruiting_body_lab'].includes(photo.photo_type);
}

/** Which record a dossier photograph was taken of, by its `source`. */
const RECORD_OF = { collection: 'collection', external: 'intake', plate: 'plate', slant: 'slant' };
/** The kind a photograph is counted under in the lightbox's chips: a field photograph is the collection's or the intake's. */
const GROUP_OF = { collection: 'field', external: 'field', plate: 'plate', slant: 'slant' };

/** Two addresses of one object, however each was written (loopback or re-based). */
function sameUrl(a, b) {
  if (!a || !b) return false;
  return a === b || resolvePhotoUrl(a) === resolvePhotoUrl(b);
}

/** A moment the server sent, in the reader's format; the text itself if unreadable. */
function moment(iso) {
  const d = new Date(String(iso));
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

/** A viewer button: PhotoViewer's dark caption, so the ghost button is drawn light. */
const ghost = {
  ...S.ghostBtn, padding: '3px 10px', fontSize: 12, background: 'rgba(255,255,255,0.15)', color: 'var(--c-on-accent)',
  border: '1px solid rgba(255,255,255,0.4)',
};
/** The chip of the kind the open photograph is. */
const groupOn = { ...ghost, background: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.9)' };
